import { useTranslation } from 'react-i18next';
import { Select } from '@/shared/ui/selects/Select';
import { cn } from '@/shared/utils/cn';

type LanguageCode = 'uz' | 'ru' | 'en';

const LANGUAGES: LanguageCode[] = ['uz', 'ru', 'en'];

const PILL_LABELS: Record<LanguageCode, string> = {
  uz: 'UZ',
  ru: 'RU',
  en: 'EN',
};

type Props = {
  variant?: 'select' | 'pills';
  className?: string;
};

export const LanguageSwitcher = ({ variant = 'select', className }: Props) => {
  const { t, i18n } = useTranslation();
  const current = (i18n.resolvedLanguage ?? i18n.language ?? 'uz').slice(0, 2) as LanguageCode;

  const change = (lang: LanguageCode) => {
    void i18n.changeLanguage(lang);
  };

  if (variant === 'pills') {
    return (
      <div
        role="group"
        aria-label={t('common.language')}
        className={cn(
          'inline-flex gap-1 rounded-full border border-slate-200 bg-white p-1',
          className,
        )}
      >
        {LANGUAGES.map((lang) => {
          const active = current === lang;
          return (
            <button
              key={lang}
              type="button"
              onClick={() => change(lang)}
              aria-pressed={active}
              className={cn(
                'inline-flex min-w-[2.25rem] cursor-pointer items-center justify-center rounded-full px-2.5 py-1 text-xs font-semibold uppercase transition-colors',
                active
                  ? 'bg-brand-600 text-white'
                  : 'text-ink-soft hover:bg-slate-100',
              )}
            >
              {PILL_LABELS[lang]}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div className={cn('w-32', className)}>
      <Select
        aria-label={t('common.language')}
        value={current}
        onChange={(e) => change(e.target.value as LanguageCode)}
        options={[
          { value: 'uz', label: t('common.uzbek') },
          { value: 'ru', label: t('common.russian') },
          { value: 'en', label: t('common.english') },
        ]}
      />
    </div>
  );
};
